import { useEffect, useMemo, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { formatCompact } from "../../lib/renderers.js";
import { useLabels, scopeStepLabel } from "../../lib/dimensionDefs.js";
import { dashboardScopeToApiScope } from "../../lib/analyticsClient.js";
import { isMemoryProjectId, labelForMemory, memoryKindFromSlug } from "../../lib/memoryNames.js";
import { listOrgLessons, memoryEndpointMatrix, memoryProjectFacet, listMemoryBodies, storeSiblingSlugs } from "../../lib/memories.js";
import { buildMemoryGraph } from "../../lib/memoryGraph.js";
import MemoryStoreMap from "./MemoryStoreMap.jsx";

// Scope-aware right-column panel for any MEMORY scope — replaces the generic
// Contrast grid (the way a signal scope shows its definition instead). It
// dispatches on the scope shape so each drill stage shows something relevant:
//   • a single memory   → its FLEET SPREAD (who has it, who's missing, samplable)
//   • everything else    → a scoped memory OVERVIEW (totals + the lessons here)
// The main column already shows the memory's content/usage/drift, so this is the
// complementary "across the fleet" view.

const EYEBROW = "text-[10px] uppercase tracking-[0.1em] text-[color:var(--muted-soft)]";

export default function MemoryScopePanel({ scopePath = [], range, onNavigatePath }) {
  const memoryStep = useMemo(() => [...scopePath].reverse().find((s) => s.dim === "memories"), [scopePath]);
  const slugs = memoryStep ? (memoryStep.ids?.length ? memoryStep.ids : memoryStep.id != null ? [memoryStep.id] : []) : [];
  const singleSlug = slugs.length === 1 && !isMemoryProjectId(slugs[0]) ? slugs[0] : null;

  if (singleSlug) {
    return <FleetSpread slug={singleSlug} scopePath={scopePath} range={range} onNavigatePath={onNavigatePath} />;
  }
  return <ScopeOverview scopePath={scopePath} range={range} onNavigatePath={onNavigatePath} />;
}

function Section({ title, hint, children }) {
  return (
    <div className="grid gap-[5px]">
      <div className="grid gap-[1px]">
        <span className={EYEBROW}>{title}</span>
        {hint ? <span className={NOTE_CLASS}>{hint}</span> : null}
      </div>
      <div className="flex flex-wrap gap-[4px]">{children}</div>
    </div>
  );
}

function SampleChip({ label, current, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Sample the traces that wrote or used this memory on this endpoint"
      className={[
        "group flex max-w-[200px] items-center gap-[4px] rounded-[3px] border bg-[color:var(--bg)] px-[5px] py-[1px] text-[10px] leading-[1.3] transition-colors",
        current ? "border-[color:var(--line-strong)] text-[color:var(--ink)]" : "border-[color:var(--line)] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]",
      ].join(" ")}
    >
      <span className="truncate">{label}</span>
      <span aria-hidden className="text-[color:var(--muted-soft)] transition-colors group-hover:text-[color:var(--green)]">↗</span>
    </button>
  );
}

function PlainChip({ label }) {
  return (
    <OverflowTooltip
      as="span"
      text={label}
      className="max-w-[200px] truncate rounded-[3px] border border-[color:var(--line-soft)] bg-[color:var(--bg)] px-[5px] py-[1px] text-[10px] leading-[1.3] text-[color:var(--muted)]"
    />
  );
}

// ── single memory: fleet spread ──────────────────────────────────────

function FleetSpread({ slug, scopePath, range, onNavigatePath }) {
  const [matrix, setMatrix] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let on = true;
    setMatrix(null);
    setErr(null);
    // Fleet-wide (apiScope []) — the memory's reach across ALL endpoints, not
    // just the current sub-scope; that's the propagation-relevant view.
    memoryEndpointMatrix({ apiScope: [], range })
      .then((mx) => { if (on) setMatrix(mx); })
      .catch((e) => { if (on) setErr(e?.message || String(e)); });
    return () => { on = false; };
  }, [range]);

  // "Same store" — the other memories in this one's store. Prefers PROJECT
  // membership (a project's MEMORY.md is the store, cross-device via the decoded
  // projectDir); falls back to the person's ENDPOINT when the memory.project facet
  // isn't populated yet or the memory is project-less. See storeSiblingSlugs.
  const [projectsBySlug, setProjectsBySlug] = useState(null);
  useEffect(() => {
    let on = true;
    memoryProjectFacet({ apiScope: [], range, by: "tag" })
      .then((m) => { if (on) setProjectsBySlug(m); })
      .catch(() => { if (on) setProjectsBySlug(new Map()); });
    return () => { on = false; };
  }, [range]);

  const siblings = useMemo(() => {
    if (!matrix) return null;
    return storeSiblingSlugs(slug, { matrix, projectsBySlug })
      .map((s) => ({ slug: s, label: labelForMemory(s), kind: memoryKindFromSlug(s) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [matrix, slug, projectsBySlug]);

  const have = useMemo(() => (matrix ? [...(matrix.slugEndpoints.get(slug) || [])] : []), [matrix, slug]);
  const missing = useMemo(() => (matrix ? [...matrix.allEndpoints].filter((e) => !have.includes(e)) : []), [matrix, have]);
  const curEp = useMemo(() => [...scopePath].reverse().find((s) => s.dim === "endpoints")?.id || null, [scopePath]);

  const epSteps = useMemo(() => [...have, ...missing].map((id) => ({ dim: "endpoints", id })), [have.join(","), missing.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const tick = useLabels(epSteps);
  const labels = useMemo(() => {
    const m = {};
    for (const id of [...have, ...missing]) m[id] = scopeStepLabel({ dim: "endpoints", id });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [have.join(","), missing.join(","), tick]);

  const sample = (epId) => onNavigatePath?.([
    { dim: "endpoints", ids: [epId], id: epId },
    { dim: "memories", ids: [slug], id: slug },
  ]);

  // "Same store" as a radial: reuse MemoryStoreMap (the index-hub map) over a
  // synthetic graph — this memory + its store siblings as index leaves. Map by
  // default, plain list as fallback.
  const [storeView, setStoreView] = useState("map"); // "map" | "list"

  // Hydrate ONLY this memory's latest body (one event) so the graph can draw the
  // focal memory's own [[wikilink]] edges to siblings — its real cross-links.
  // Bounded on purpose: hydrating all ~N siblings would be far too heavy for a
  // panel. No body (or no links) → the graph degrades to the clean index star.
  // Cold-path gate: the body only feeds the radial, so skip the fetch entirely
  // while the user is on the List view (re-hydrates if they switch to Map).
  const [body, setBody] = useState(null);
  useEffect(() => {
    setBody(null);
    if (storeView !== "map") return;
    let on = true;
    (async () => {
      try {
        // The focal memory's latest full-content body from the `memory` signal
        // (fleet-wide, apiScope []) → its [[wikilink]] edges. No body → clean star.
        const bodies = await listMemoryBodies({ apiScope: [], range, slug, limit: 20 });
        const full = bodies.find((x) => (x.op === "write" || x.op === "read") && x.body);
        if (on && full) setBody(full.body);
      } catch { /* no body → graph stays a star */ }
    })();
    return () => { on = false; };
  }, [slug, range, storeView]);

  const storeGraph = useMemo(() => {
    if (!siblings || !siblings.length) return null;
    const entries = [{ slug, title: labelForMemory(slug) }, ...siblings.map((f) => ({ slug: f.slug, title: f.label }))];
    return buildMemoryGraph({
      indexEntries: entries,
      capturedSlugs: new Set(entries.map((e) => e.slug)),
      bodies: body ? new Map([[slug, body]]) : undefined,
      current: slug,
    });
  }, [siblings, slug, body]);

  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[12px] px-[2px] py-[10px]">
      <div className="grid gap-[2px]">
        <span className={EYEBROW}>Memory · fleet spread</span>
        <div className="text-[14px] leading-[1.18] font-medium text-[color:var(--ink)]">{labelForMemory(slug)}</div>
        <div className="text-[11px] leading-[1.3] text-[color:var(--muted)]">{memoryKindFromSlug(slug)}</div>
      </div>

      {err ? (
        <div className="text-[11px] text-red-400">Couldn&apos;t load spread: {err}</div>
      ) : !matrix ? (
        <div className={`${NOTE_CLASS} animate-pulse`}>Loading the memory&apos;s spread across the fleet…</div>
      ) : (
        <>
          <Section title={`Has it · ${have.length} endpoint${have.length === 1 ? "" : "s"}`} hint="Click to sample the traces that wrote or used it there">
            {have.map((id) => <SampleChip key={id} label={labels[id] || id} current={id === curEp} onClick={() => sample(id)} />)}
          </Section>
          {missing.length ? (
            <div className="grid gap-[5px] border-t border-[color:var(--line-soft)] pt-[8px]">
              <span className="text-[10px] uppercase tracking-[0.1em] text-[color:var(--eyebrow)]">
                Missing · {missing.length} endpoint{missing.length === 1 ? "" : "s"}
              </span>
              <div className="flex flex-wrap gap-[4px]">
                {missing.map((id) => <PlainChip key={id} label={labels[id] || id} />)}
              </div>
            </div>
          ) : (
            <div className={`${NOTE_CLASS} border-t border-[color:var(--line-soft)] pt-[8px]`}>Held by every endpoint that writes memories.</div>
          )}
        </>
      )}

      {siblings && siblings.length ? (
        <div className="grid gap-[6px] border-t border-[color:var(--line-soft)] pt-[8px]">
          <div className="flex items-start justify-between gap-[8px]">
            <div className="grid gap-[1px]">
              <span className={EYEBROW}>Same store · {siblings.length} other memor{siblings.length === 1 ? "y" : "ies"}</span>
              <span className={NOTE_CLASS}>Other lessons the index carrying this one also lists</span>
            </div>
            <div className="flex shrink-0 overflow-hidden rounded-[3px] border border-[color:var(--line)]">
              {["map", "list"].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setStoreView(v)}
                  className={[
                    "px-[7px] py-[2px] text-[10px] capitalize leading-[1.2] transition-colors",
                    storeView === v ? "bg-[color:var(--control-bg)] text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                  ].join(" ")}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {storeView === "map" && storeGraph ? (
            // Cap the CHART (the svg caps its own width+height at maxHeight and
            // centers) while this section + the legend stay full-width — so the
            // circle is a sensibly-sized centered square with no letterbox and no
            // capped-container indent. Caption suppressed: the section header
            // already says "Same store · N other memories".
            <MemoryStoreMap
              graph={storeGraph}
              current={slug}
              anchor={slug}
              showCaption={false}
              onNavigate={(s) => onNavigatePath?.([{ dim: "memories", ids: [s], id: s }])}
            />
          ) : (
            <div className="grid gap-[3px]">
              {siblings.slice(0, 14).map((f) => (
                <button
                  key={f.slug}
                  type="button"
                  onClick={() => onNavigatePath?.([{ dim: "memories", ids: [f.slug], id: f.slug }])}
                  className="flex items-center justify-between gap-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[5px] text-left transition-colors hover:border-[color:var(--line-strong)]"
                >
                  <span className="min-w-0 truncate text-[11px] leading-[1.2] text-[color:var(--ink)]">{f.label}</span>
                  <span className="shrink-0 text-[10px] leading-[1.2] text-[color:var(--muted-soft)]">{f.kind}</span>
                </button>
              ))}
              {siblings.length > 14 ? <span className={NOTE_CLASS}>+ {siblings.length - 14} more in the store</span> : null}
            </div>
          )}
        </div>
      ) : null}
    </ScrollArea>
  );
}

// ── store / endpoint / project: scoped overview ──────────────────────

const KIND_ORDER = ["Feedback", "Reference", "Project", "User", "Environment", "Note"];

function ScopeOverview({ scopePath, range, onNavigatePath }) {
  const scopeKeyStr = useMemo(() => JSON.stringify(scopePath), [scopePath]);
  const apiScope = useMemo(() => dashboardScopeToApiScope(scopePath).apiScope, [scopeKeyStr]); // eslint-disable-line react-hooks/exhaustive-deps
  const [lessons, setLessons] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let on = true;
    setLessons(null);
    setErr(null);
    listOrgLessons({ apiScope, range, limit: 600 })
      .then((rows) => { if (on) setLessons(rows); })
      .catch((e) => { if (on) setErr(e?.message || String(e)); });
    return () => { on = false; };
  }, [scopeKeyStr, range]); // eslint-disable-line react-hooks/exhaustive-deps

  const kinds = useMemo(() => {
    if (!lessons) return [];
    const m = {};
    for (const l of lessons) m[l.kind] = (m[l.kind] || 0) + 1;
    return KIND_ORDER.filter((k) => m[k]).map((k) => [k, m[k]]);
  }, [lessons]);

  // Surface the most shared first (propagation candidates), then most active.
  const ranked = useMemo(() => {
    if (!lessons) return [];
    return [...lessons].sort((a, b) => (b.endpointCount || 0) - (a.endpointCount || 0) || (b.value || 0) - (a.value || 0)).slice(0, 20);
  }, [lessons]);

  const open = (slug) => onNavigatePath?.([...scopePath, { dim: "memories", ids: [slug], id: slug }]);

  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[12px] px-[2px] py-[10px]">
      <div className="grid gap-[2px]">
        <span className={EYEBROW}>Memory · overview</span>
        <div className="text-[14px] leading-[1.18] font-medium text-[color:var(--ink)]">
          {lessons ? `${formatCompact(lessons.length)} memories in scope` : "Memories in scope"}
        </div>
        {kinds.length ? (
          <div className="flex flex-wrap gap-x-[8px] gap-y-[1px] text-[11px] leading-[1.3] text-[color:var(--muted)]">
            {kinds.map(([k, n]) => <span key={k}>{k} <span className="text-[color:var(--muted-soft)]">{n}</span></span>)}
          </div>
        ) : null}
      </div>

      {err ? (
        <div className="text-[11px] text-red-400">Couldn&apos;t load memories: {err}</div>
      ) : !lessons ? (
        <div className={`${NOTE_CLASS} animate-pulse`}>Reading memories in scope…</div>
      ) : !ranked.length ? (
        <div className={NOTE_CLASS}>No memories in this scope.</div>
      ) : (
        <div className="grid gap-[5px]">
          <span className={EYEBROW}>Most shared first</span>
          <div className="grid gap-[3px]">
            {ranked.map((l) => (
              <button
                key={l.slug}
                type="button"
                onClick={() => open(l.slug)}
                className="group flex items-center justify-between gap-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[5px] text-left transition-colors hover:border-[color:var(--line-strong)]"
              >
                <span className="min-w-0 truncate text-[11px] leading-[1.2] text-[color:var(--ink)]">{l.label}</span>
                <span className="shrink-0 text-[10px] leading-[1.2] text-[color:var(--muted-soft)]">
                  {l.endpointCount > 1 ? `${l.endpointCount} endpoints` : "siloed"}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </ScrollArea>
  );
}
